// ITAM-2887: error mapping for the advisor profile.
//
// The backend returns a STRUCTURED body for advisor failures: `400 {errorCode,
// message}` from GlobalExceptionHandler with the codes in AssociationErrorCode
// (never 401/403). Bean validation failures come back as a `400 {field:
// message}` map. A missing profile row is a 404 on the profile GET and on the
// availability endpoint.

/** The shape this codebase's axios rejections actually have. */
type RejectionLike = {
  response?: {
    status?: number;
    data?: { errorCode?: string; message?: string } & Record<string, unknown>;
  };
};

const rejection = (error: unknown): RejectionLike =>
  typeof error === "object" && error !== null ? (error as RejectionLike) : {};

const status = (error: unknown): number | undefined =>
  rejection(error).response?.status;

const errorCode = (error: unknown): string | undefined =>
  rejection(error).response?.data?.errorCode;

const backendMessage = (error: unknown): string | undefined => {
  const message = rejection(error).response?.data?.message;
  return typeof message === "string" && message.trim() ? message : undefined;
};

/**
 * A bean-validation body is a flat `{field: message}` map with no errorCode.
 * The first string value is the most useful thing to show.
 */
const firstValidationMessage = (error: unknown): string | undefined => {
  const data = rejection(error).response?.data;
  if (!data || typeof data !== "object" || data.errorCode) {
    return undefined;
  }
  const first = Object.values(data).find(
    (value) => typeof value === "string" && value.trim(),
  );
  return typeof first === "string" ? first : undefined;
};

export const AVAILABILITY_NO_PROFILE = "Sačuvajte profil pre aktivacije.";
export const ADVISOR_ROLE_MISSING =
  "Nalog nema ulogu savetnika, obratite se podršci.";
export const GENERIC_SAVE_ERROR =
  "Čuvanje nije uspelo. Pokušajte ponovo, a ako se ponovi, obratite se podršci.";
export const GENERIC_LOAD_ERROR = "Učitavanje nije uspelo. Pokušajte ponovo.";
export const PROFILE_GONE_ERROR = "Profil više ne postoji. Kreirajte ga ponovo.";
export const INVALID_INPUT_ERROR =
  "Podaci nisu prihvaćeni. Proverite unos i pokušajte ponovo.";

/** Backend `AssociationErrorCode.ADVISOR_PROFILE_ALREADY_EXISTS`, always a 400. */
export const isProfileAlreadyExists = (error: unknown): boolean =>
  errorCode(error) === "ADVISOR_PROFILE_ALREADY_EXISTS";

/** Backend `AssociationErrorCode.ADVISOR_ROLE_REQUIRED`, always a 400. */
export const isAdvisorRoleRequired = (error: unknown): boolean =>
  errorCode(error) === "ADVISOR_ROLE_REQUIRED";

export const mapAvailabilityError = (error: unknown): string => {
  if (isAdvisorRoleRequired(error)) {
    return ADVISOR_ROLE_MISSING;
  }
  if (status(error) === 404) {
    // The availability endpoint needs an existing profile row.
    return AVAILABILITY_NO_PROFILE;
  }
  return GENERIC_SAVE_ERROR;
};

/**
 * True when the caller must REFETCH before rendering anything else: a 400 we
 * hold no code for tells us nothing about what the server did, and a 404 says
 * the row is gone. Both are answered by re-reading the truth.
 */
export const shouldRefetchAfterError = (error: unknown): boolean => {
  const code = status(error);
  return code === 400 || code === 404;
};

/**
 * Deliberately has NO 404 branch. A 404 only tells us the save failed, not
 * what the server holds now; the caller decides after its reconciliation GET
 * and uses PROFILE_GONE_ERROR only once absence is a confirmed fact.
 */
export const mapProfileSaveError = (error: unknown): string => {
  if (isAdvisorRoleRequired(error)) {
    return ADVISOR_ROLE_MISSING;
  }
  if (status(error) === 400) {
    const code = errorCode(error);
    if (code === "ASSOCIATION_INVALID_INPUT") {
      return backendMessage(error) ?? INVALID_INPUT_ERROR;
    }
    // Ownership and server-side storage failures say nothing about the input.
    if (
      code === "ASSOCIATION_NOT_ADMINISTERED" ||
      code === "ASSOCIATION_MEDIA_UPLOAD_FAILED"
    ) {
      return GENERIC_SAVE_ERROR;
    }
    return firstValidationMessage(error) ?? INVALID_INPUT_ERROR;
  }
  return GENERIC_SAVE_ERROR;
};

/** Like `mapProfileSaveError`, no 404 branch, for the same reason. */
export const mapWorkingHoursError = (error: unknown): string => {
  if (status(error) === 400) {
    return (
      firstValidationMessage(error) ??
      "Radno vreme nije prihvaćeno. Proverite unos i pokušajte ponovo."
    );
  }
  return GENERIC_SAVE_ERROR;
};
